import { useEffect, useRef } from 'react';
import { postNavigationHeartbeat } from '../services/accessReportApi';
import { useAuth } from '../contexts/AuthContext';

const SESSION_STORAGE_KEY = 'baruk.go.nav.session';
const HEARTBEAT_INTERVAL_MS = 60_000;

function resolveClientSessionKey(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing && existing.trim()) {
      return existing.trim();
    }
    const created = crypto.randomUUID().replace(/-/g, '');
    sessionStorage.setItem(SESSION_STORAGE_KEY, created);
    return created;
  } catch {
    return `tmp-${Date.now()}`;
  }
}

/**
 * Envia heartbeat de navegação enquanto o usuário autenticado mantém a aba visível.
 */
export function useNavigationHeartbeat(): void {
  const { isAuthenticated } = useAuth();
  const keyRef = useRef<string>(resolveClientSessionKey());

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let cancelled = false;

    const send = async () => {
      if (cancelled || document.visibilityState === 'hidden') {
        return;
      }
      try {
        await postNavigationHeartbeat(keyRef.current);
      } catch {
        // Silencioso: não interrompe a UI se o heartbeat falhar.
      }
    };

    void send();
    const timer = window.setInterval(() => {
      void send();
    }, HEARTBEAT_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void send();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated]);
}
